import { toWSDateString } from "@wingspanhq/fe-component-library";
import {
  PayableRow,
  SearchPayableRowsFilterParameter
} from "@wingspanhq/wingspan-api-sdk";
import { useCallback, useState } from "react";
import { queriesPayableRows } from "@query";
import { selectorSearchRowName } from "../../../shared/selectors/selectorSearchRowName";

export type UseDownloadPayablesCsvArgs = {
  filter: SearchPayableRowsFilterParameter;
};

type PayableLineItem = NonNullable<PayableRow["lineItems"]>[number];

const PAGE_SIZE = 100;
const MAX_PAGES = 200;

const formatDate = (
  date: Date | undefined | null,
  format?: Parameters<typeof toWSDateString>[1]
): string | undefined => (date ? toWSDateString(date, format) : undefined);

const joinLineItemField = (
  map: (lineItem: PayableLineItem, index: number) => unknown,
  lineItems: PayableLineItem[] | undefined
): string | undefined => {
  if (!lineItems || lineItems.length === 0) {
    return undefined;
  }

  const mapped = lineItems
    .map(map)
    .filter(value => value !== undefined && value !== "" && value !== null);

  return mapped.length ? mapped.join(", ") : undefined;
};

type CsvContext = {
  payable: PayableRow;
  timezone: string;
};

type CsvColumn = {
  header: string;
  get: (ctx: CsvContext) => unknown;
};

// Column set is the v1 payables CSV report intersected with the fields the v3
// `PayableRow` carries. Columns that read v1-only data (attachments, notes,
// most workflow event timestamps, per-line-item cost breakdown and custom
// labels, contractor external id / preferred name) are dropped — recorded as an
// accepted regression in V3_MIGRATION_TRACKER.md.
const CSV_COLUMNS: CsvColumn[] = [
  { header: "Payable ID", get: ({ payable }) => payable.id },
  { header: "Payable Number", get: ({ payable }) => payable.externalId },
  {
    header: "Contractor Name",
    get: ({ payable }) => selectorSearchRowName(payable)
  },
  {
    header: "Contractor First Name",
    get: ({ payable }) => payable.profile?.firstName
  },
  {
    header: "Contractor Last Name",
    get: ({ payable }) => payable.profile?.lastName
  },
  {
    header: "Contractor Company Name",
    get: ({ payable }) =>
      payable.profile?.companyName ?? payable.profile?.doingBusinessAs
  },
  { header: "Contractor Email", get: ({ payable }) => payable.email },
  { header: "Payee ID", get: ({ payable }) => payable.payeeId },
  { header: "Payer ID", get: ({ payable }) => payable.payerId },
  { header: "Amount", get: ({ payable }) => payable.amount },
  { header: "Currency", get: ({ payable }) => payable.currency?.toUpperCase() },
  {
    header: "Purchase Order Number",
    get: ({ payable }) => payable.metadata?.purchaseOrderNumber
  },
  {
    header: "Due Date",
    get: ({ payable }) => formatDate(payable.dueDate, "input")
  },
  {
    header: "Scheduled Payment Date",
    get: ({ payable }) => formatDate(payable.scheduledPaymentDate, "input")
  },
  {
    header: "Opened Date",
    get: ({ payable }) => formatDate(payable.events?.openedAt)
  },
  {
    header: "Paid Date",
    get: ({ payable }) => formatDate(payable.events?.paidAt)
  },
  {
    header: "Deposit Confirmed Date",
    get: ({ payable }) => formatDate(payable.events?.depositConfirmedAt)
  },
  {
    header: "Cancelled Date",
    get: ({ payable }) => formatDate(payable.events?.cancelledAt)
  },
  { header: "Timezone", get: ({ timezone }) => timezone },
  {
    header: "Contractor Review Status",
    get: ({ payable }) => payable.payeeReviewStatus
  },
  {
    header: "Approval Status",
    get: ({ payable }) => payable.payerApprovalStatus
  },
  { header: "Status", get: ({ payable }) => payable.status },
  {
    header: "Project Name",
    get: ({ payable }) => payable.metadata?.projectName
  },
  {
    header: "Line Item Index",
    get: ({ payable }) =>
      joinLineItemField((_lineItem, index) => index, payable.lineItems)
  },
  {
    header: "Line Item Title",
    get: ({ payable }) =>
      joinLineItemField(lineItem => lineItem.description, payable.lineItems)
  },
  {
    header: "Line Item Amount",
    get: ({ payable }) =>
      joinLineItemField(
        lineItem => lineItem.amount?.toFixed(2),
        payable.lineItems
      )
  }
];

const escapeCell = (value: unknown): string => {
  if (value === undefined || value === null) {
    return '""';
  }
  const stringified = String(value)
    .replace(/"/g, '""')
    .replace(/[\r\n]+/g, " ");
  return `"${stringified}"`;
};

const toCsv = (payables: PayableRow[]): string => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const headerRow = CSV_COLUMNS.map(column => column.header)
    .map(escapeCell)
    .join(",");

  const dataRows = payables.map(payable =>
    CSV_COLUMNS.map(column =>
      escapeCell(column.get({ payable, timezone }))
    ).join(",")
  );

  return [headerRow, ...dataRows].join("\n");
};

const triggerDownload = (csv: string, filename: string): void => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

export const useDownloadPayablesCsv = (args: UseDownloadPayablesCsvArgs) => {
  const [isLoading, setIsLoading] = useState(false);

  const download = useCallback(async () => {
    setIsLoading(true);
    try {
      const collected: PayableRow[] = [];
      let token: string | undefined = undefined;
      let pageCount = 0;

      do {
        pageCount++;
        const page = await queriesPayableRows.searchPayableRows.fetchQueryData({
          filter: args.filter,
          page: { size: PAGE_SIZE, token }
        });
        collected.push(...(page.data ?? []));
        token = page.pagination?.nextPageToken ?? undefined;
      } while (token && pageCount < MAX_PAGES);

      const csv = toCsv(collected);
      const filename = `payables-${new Date().toISOString().slice(0, 10)}.csv`;
      triggerDownload(csv, filename);
    } finally {
      setIsLoading(false);
    }
  }, [args.filter]);

  return {
    download,
    isLoading,
    isDisabled: false
  };
};
